import {BaseDto, Response} from "../interfaces/common.interface";
import {LoggingService} from "../services/log.service";
import {RestfulService} from "../services/restful.service";
import {Observable, Subject, Subscription} from "rxjs";
import {Command} from "./viewTracker.module";
import {ListRequestBody, RequestBody} from "../interfaces/admin.interface";
import {HttpParams, HttpResponse} from "@angular/common/http";
import {getEndPointWithParams} from "../../../assets/js/util";

export interface Path {
    find?: string
    findInfo?: string
    filter?: string
    create?: string
    update?: string
    toggle?: string

}

export class EndPoint {

    baseUrl: string
    paths: Path

    constructor(baseUrl: string, standard: boolean = false, paths?: []) {
        this.baseUrl = baseUrl;
        if(standard) {
            this.paths = {
                find: `/${baseUrl}/all`,
                findInfo: `/${baseUrl}/:id`,
                filter: `/${baseUrl}/filter`,
                create: `/${baseUrl}`,
                update: paths && paths['update'] ? `${paths['update']}` : `/${baseUrl}/:id`,
                toggle: `/${baseUrl}/toggle/:id`
            }
        }
        else {
            this.paths = {
                find: `/${baseUrl}/all`,
                findInfo: `/${baseUrl}/info/:id`,
                filter: `/${baseUrl}/filter`,
                create: `/${baseUrl}/create`,
                update: paths && paths['update'] ? `${paths['update']}` : `/${baseUrl}/update`,
                toggle: `/${baseUrl}/toggle/:id`
            }
        }
    }

    fill(endPoint: string) {
        return `/${this.baseUrl}${endPoint}`
    }

    push(endPointKey: string, endPoint: string) {
        this.paths[endPointKey] = this.fill(endPoint)
    }

}

export interface ListService<T extends ListRequestBody> {
    list(reqBody: RequestBody): Subscription;
}

export abstract class ServiceRoot {

    protected endPoint: EndPoint

    constructor(protected logger: LoggingService, protected restful: RestfulService, endPoint?: EndPoint) {
        this.endPoint = endPoint
    }

    protected post(endPoint: string, reqBody: any, localhost: boolean = false): Observable<any> {
        return new Observable((observer) => {
            this.restful.post(endPoint, reqBody).subscribe((response: any) => {
                this.responseHandler(response, observer);
            });
        });
    }

    protected postMultipart(endPoint: string, reqBody: any, localhost: boolean = false): Observable<any> {
        return new Observable((observer) => {
            this.restful.postMultipart(endPoint, reqBody).subscribe((response: any) => {
                this.responseHandler(response, observer);
            });
        });
    }

    protected put(endPoint: string, reqBody: any, localhost: boolean = false): Observable<any> {
        return new Observable((observer) => {
            this.restful.put(endPoint, reqBody).subscribe((response: any) => {
                this.responseHandler(response, observer);
            });
        });
    }

    protected get(endPoint: string): Observable<any> {
        return new Observable((observer) => {
            this.restful.get(endPoint).subscribe((response: any) => {
                this.responseHandler(response, observer);
            });
        });
    }

    protected getWithParams(endPoint: string, params: HttpParams | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
    }): Observable<any> {
        return new Observable((observer) => {
            this.restful.getWithParams(endPoint, params).subscribe((response: any) => {
                this.responseHandler(response, observer);
            });
        });
    }

    protected responseHandler<T>(response: HttpResponse<Response<T>>, observer: any): void {
        const outcome = response["outcome"];
        if (outcome.success === true) {
            this.logger.log("Service:", "SUCCESS", 200);
            observer.next(response);
            observer.complete();
        } else {
            this.logger.log("Service:", "FAILURE", 200);
            observer.next(response);
            observer.complete();
            // outcome.message = this.utils.manageErrorMsg(outcome);
            // observer.error(outcome);
        }
    }
}

export abstract class BaseService<Dto extends BaseDto<Dto, Req>, Req> extends ServiceRoot {

    private _lastSearch: any | null

    protected loadObject = new Subject<Command<Dto>>();
    // Observable string streams
    protected objectLoaded$ = this.loadObject.asObservable();

    protected loadObjects = new Subject<Command<Array<Dto>>>();
    // Observable string streams
    protected objectsLoaded$ = this.loadObjects.asObservable();

    constructor(logger: LoggingService, restful: RestfulService, endPoint?: EndPoint) {
        super(logger, restful, endPoint)
    }

    protected abstract getDefaultDto() : Dto;

    public executeWhenObjectLoaded(callback: (callback: Command<Dto>) => void) {
        return this.objectLoaded$.subscribe(callback)
    }

    public executeWhenObjectsLoaded(callback: (callback: Command<Array<Dto>>) => void) {
        return this.objectsLoaded$.subscribe(callback)
    }

    public setNew() {
        const cmd =
            Command.forNew(new Response<Dto>(this.getDefaultDto()))
        setTimeout(() => this.loadObject.next(cmd), 10)
        // this.loadObject.next(cmd)
    }

    abstract getRecord(id: number): Observable<any>;

    protected listImpl(endPoint : string, reqBody: RequestBody) {
        const subsrc = this.post(endPoint, reqBody).subscribe(value => {
            this.loadObjects.next(Command.forList(value))
            subsrc.unsubscribe();
        });
        return subsrc
    }

    public filter(filter: string, reqBody: RequestBody): Observable<any> {
        reqBody.filter = filter;
        return this.post(this.endPoint.paths.filter, reqBody);
    }

    protected getRecordImpl(id: number, baseEndPoint: string): Observable<any> {
        const endPoint = getEndPointWithParams(baseEndPoint, id);
        const getter = this.get(endPoint);
        const subscr = getter.subscribe(value => {
            this.loadObject.next(Command.forEdit(value))
            subscr.unsubscribe();
        });
        return getter;
    }

    private loaded(value: any) {
        this.loadObject.next(Command.forEdit(value))
    }

    protected saveImpl(reqBody: Req, endPoint: string, id?: number) {
        if (id != null) {
            const subsrc = this.put(getEndPointWithParams(endPoint, id), reqBody).subscribe(value => {
                this.loadObject.next(Command.updated(value))
                subsrc.unsubscribe();
            });
        } else {
            const subsrc = this.post(endPoint, reqBody).subscribe(value => {
                this.loadObject.next(Command.created(value))
                subsrc.unsubscribe();
            });
        }
    }

    public toggle(id: number) {
        const endPoint = getEndPointWithParams(this.endPoint.paths.toggle, id);
        const subsrc = this.put(endPoint, {}).subscribe(value => {
            this.loadObject.next(Command.forEdit(value))
            subsrc.unsubscribe();
        });
    }

    public toggleAsync(id: number, callback: () => void) {
        const endPoint = getEndPointWithParams(this.endPoint.paths.toggle, id);
        const subsrc = this.put(endPoint, {}).subscribe(value => {
            callback()
            subsrc.unsubscribe();
        });
    }

    delete(endPoint: string): Observable<any> {
        return Observable.create((observer) => {
            this.restful.delete(endPoint).subscribe((response: any) => {
                this.responseHandler(response, observer);
            });
        });
    }

    get lastSearch(): any {
        return this._lastSearch;
    }

    set lastSearch(value: any) {
        this._lastSearch = value;
    }
}